import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Edit, 
  Save, 
  X, 
  Camera,
  Building,
  Calendar,
  Award,
  Star
} from 'lucide-react';
import { EnhancedHeader } from '../common/EnhancedHeader';
import { API_BASE } from '@/config/api';
import InternationalPhoneInput from '../common/InternationalPhoneInput';
import TailorCalendarManager from './TailorCalendarManager';

interface BusinessProfileData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: {
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  businessName?: string;
  businessType?: string;
  experience?: number;
  specialties?: string[];
  bio?: string;
  avatar?: string;
  rating?: number;
  totalOrders?: number;
  joinDate?: string;
  gstRegistrationStatus?: 'registered' | 'unregistered' | 'composite_scheme';
  gstNumber?: string;
}

const BusinessProfile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<BusinessProfileData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<BusinessProfileData>>({});
  const [activeTab, setActiveTab] = useState<'profile' | 'calendar'>('profile');

  useEffect(() => {
    if (user) {
      // Initialize profile data with user data
      setProfileData({
        id: user.id,
        name: user.name || user.full_name || 'Business User',
        email: user.email,
        phone: user.phone || '',
        businessName: user.business_name || '',
        businessType: user.business_type || 'Tailoring',
        experience: user.experience || 0,
        specialties: user.specialties || ['Suits', 'Formal Wear'],
        bio: user.bio || 'Professional tailor with years of experience in custom tailoring.',
        avatar: user.avatar_url || '',
        rating: 4.8,
        totalOrders: 0,
        joinDate: user.created_at || new Date().toISOString(),
        gstRegistrationStatus: user.gst_registration_status || 'unregistered',
        gstNumber: user.gst_number || ''
      });
      setFormData({
        name: user.name || user.full_name || 'Business User',
        email: user.email,
        phone: user.phone || '',
        businessName: user.business_name || '',
        businessType: user.business_type || 'Tailoring',
        experience: user.experience || 0,
        specialties: user.specialties || ['Suits', 'Formal Wear'],
        bio: user.bio || 'Professional tailor with years of experience in custom tailoring.',
        gstRegistrationStatus: user.gst_registration_status || 'unregistered',
        gstNumber: user.gst_number || ''
      });
      setLoading(false);
    }
  }, [user]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: profileData?.name,
      email: profileData?.email,
      phone: profileData?.phone,
      businessName: profileData?.businessName,
      businessType: profileData?.businessType,
      experience: profileData?.experience,
      specialties: profileData?.specialties,
      bio: profileData?.bio
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/tailors/profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setProfileData(prev => prev ? { ...prev, ...formData } : null);
        setIsEditing(false);
      } else {
        console.error('Failed to update profile');
      }
    } catch (_error) {
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecialtyChange = (index: number, value: string) => {
    const newSpecialties = [...(formData.specialties || [])];
    newSpecialties[index] = value;
    setFormData(prev => ({ ...prev, specialties: newSpecialties }));
  };

  const addSpecialty = () => {
    setFormData(prev => ({ 
      ...prev, 
      specialties: [...(prev.specialties || []), ''] 
    }));
  };

  const removeSpecialty = (index: number) => {
    const newSpecialties = [...(formData.specialties || [])];
    newSpecialties.splice(index, 1);
    setFormData(prev => ({ ...prev, specialties: newSpecialties }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile not found</h2>
          <button
            onClick={() => navigate('/business/dashboard')}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <EnhancedHeader 
        title="Business Profile"
        showBackButton={true}
        backTo="/business/dashboard"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-1">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Profile Information
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Calendar Management
            </button>
          </div>
        </div>

        {activeTab === 'calendar' ? (
          <TailorCalendarManager />
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8">
            <div className="flex items-center space-x-6">
              <div className="relative">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg">
                  {profileData.avatar ? (
                    <img 
                      src={profileData.avatar} 
                      alt="Profile" 
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-12 h-12 text-blue-600" />
                  )}
                </div>
                {isEditing && (
                  <button className="absolute bottom-0 right-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600">
                    <Camera className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              <div className="flex-1 text-white">
                <h1 className="text-3xl font-bold">{profileData.name}</h1>
                <p className="text-blue-100 text-lg">{profileData.businessName}</p>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-sm">{profileData.rating}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Award className="w-4 h-4" />
                    <span className="text-sm">{profileData.experience} years experience</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Building className="w-4 h-4" />
                    <span className="text-sm">{profileData.totalOrders} orders</span>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 flex items-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-gray-100 flex items-center space-x-2 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      <span>{saving ? 'Saving...' : 'Save'}</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleEdit}
                    className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 flex items-center space-x-2"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit Profile</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Basic Information */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">Basic Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{profileData.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{profileData.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    {isEditing ? (
                      <InternationalPhoneInput
                        value={formData.phone || ''}
                        onChange={(phone, isValid) => {
                          handleInputChange('phone', phone);
                          if (!isValid && phone) {
                            // Handle validation error if needed
                          }
                        }}
                        placeholder="Enter phone number"
                        defaultCountry="in"
                      />
                    ) : (
                      <p className="text-gray-900">{profileData.phone || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.businessName || ''}
                        onChange={(e) => handleInputChange('businessName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{profileData.businessName || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                    {isEditing ? (
                      <select
                        value={formData.businessType || ''}
                        onChange={(e) => handleInputChange('businessType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="Tailoring">Tailoring</option>
                        <option value="Fashion Design">Fashion Design</option>
                        <option value="Alterations">Alterations</option>
                        <option value="Custom Clothing">Custom Clothing</option>
                      </select>
                    ) : (
                      <p className="text-gray-900">{profileData.businessType}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Experience (Years)</label>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        value={formData.experience || 0}
                        onChange={(e) => handleInputChange('experience', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{profileData.experience} years</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Details */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">Business Details</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    {isEditing ? (
                      <textarea
                        value={formData.bio || ''}
                        onChange={(e) => handleInputChange('bio', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Tell customers about your business..."
                      />
                    ) : (
                      <p className="text-gray-900">{profileData.bio}</p>
                    )}
                  </div>

                  {/* GST Registration Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">GST Registration Status</label>
                      {isEditing ? (
                        <select
                          value={formData.gstRegistrationStatus || 'unregistered'}
                          onChange={(e) => handleInputChange('gstRegistrationStatus', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="unregistered">Unregistered</option>
                          <option value="registered">Registered</option>
                          <option value="composite_scheme">Composite Scheme</option>
                        </select>
                      ) : (
                        <p className="text-gray-900 capitalize">
                          {profileData.gstRegistrationStatus?.replace('_', ' ') || 'Unregistered'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.gstNumber || ''}
                          onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter GST number (if registered)"
                          disabled={formData.gstRegistrationStatus === 'unregistered'}
                        />
                      ) : (
                        <p className="text-gray-900">{profileData.gstNumber || 'Not provided'}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specialties</label>
                    {isEditing ? (
                      <div className="space-y-2">
                        {(formData.specialties || []).map((specialty, index) => (
                          <div key={index} className="flex space-x-2">
                            <input
                              type="text"
                              value={specialty}
                              onChange={(e) => handleSpecialtyChange(index, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Specialty"
                            />
                            <button
                              onClick={() => removeSpecialty(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={addSpecialty}
                          className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-dashed border-blue-300 w-full"
                        >
                          + Add Specialty
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {profileData.specialties?.map((specialty, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
                    <p className="text-gray-900">
                      {profileData.joinDate ? new Date(profileData.joinDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        )}
      </div>
    </div>
  );
};

export default BusinessProfile;
